import React, { useEffect, useRef, useState } from 'react';
import * as monaco from 'monaco-editor';
import styles from './CodeEditor.module.scss';

type SupportedLanguage = 'plaintext' | 'json' | 'yaml';

interface ICodeEditorProps {
  code: string;
  language: SupportedLanguage;
  onChange?: (newValue: string) => void;
  height?: number;
  width?: string | number;
  readOnly?: boolean;
  maxHeight?: number;
  placeholder?: string;
}

export const CodeEditor: React.FC<ICodeEditorProps> = ({
  code,
  language,
  onChange,
  height = 100,
  width = '100%',
  readOnly = false,
  maxHeight = 600,
  placeholder,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const modelRef = useRef<monaco.editor.ITextModel | null>(null);
  const [editorHeight, setEditorHeight] = useState(height);

  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const updateHeight = () => {
    const editor = editorRef.current;
    if (!editor) return;

    const contentHeight = editor.getContentHeight();
    const clampedHeight = Math.min(Math.max(contentHeight, height), maxHeight);
    if (clampedHeight !== editorHeight) {
      setEditorHeight(clampedHeight);
      editor.layout();
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (editorRef.current) {
      editorRef.current.dispose();
    }
    if (modelRef.current) {
      modelRef.current.dispose();
    }

    const model = monaco.editor.createModel(code, language);
    modelRef.current = model;

    const editor = monaco.editor.create(container, {
      model,
      readOnly,
      minimap: { enabled: false },
      lineNumbers: 'on',
      scrollBeyondLastLine: false,
      automaticLayout: true,
      lineNumbersMinChars: 2,
      lineDecorationsWidth: 0,
      placeholder,
    });
    editorRef.current = editor;

    const disposable = model.onDidChangeContent(() => {
      const newValue = model.getValue();
      onChangeRef.current?.(newValue);
      updateHeight();
    });

    setTimeout(() => {
      updateHeight();
      editor.layout();
    }, 0);

    return () => {
      disposable.dispose();
      editor.dispose();
      model.dispose();
    };
  }, [language, readOnly]);

  useEffect(() => {
    const model = modelRef.current;
    if (model && model.getValue() !== code) {
      model.setValue(code);
      updateHeight();
    }
  }, [code]);

  return (
    <div className={styles.editor} style={{ width }}>
      <div
        ref={containerRef}
        style={{
          height: editorHeight,
          transition: 'height 0.2s ease',
        }}
      />
    </div>
  );
};
